import { useEffect, type FC } from 'react';

import {
  Button,
  Heading,
  ModalBase,
  ModalCloseButton,
  ModalPortal,
  Text,
} from '@livechat/design-system-react-components';

import { isDesktopAppDetected } from 'helpers/desktop-app/is-detected';
import { StackingContextLevel } from 'styles/stacking-context-level';

import { LEGACY_MIGRATION_MODAL_COPY, LEGACY_MIGRATION_MODAL_TITLE, LegacyMigrationEvent } from './constants';
import { downloadTextDesktopApp, trackLegacyMigrationEvent } from './helpers';

import * as styles from './styles';

interface Props {
  imageSource: string;
  onClose(): void;
  onPrimaryAction(): void;
}

export const LegacyMigrationModal: FC<Props> = ({ imageSource, onClose, onPrimaryAction }) => {
  useEffect(() => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalDisplayed, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'CTA text': isDesktopAppDetected()
        ? LEGACY_MIGRATION_MODAL_COPY.downloadTextApp
        : LEGACY_MIGRATION_MODAL_COPY.primaryAction,
    });
  }, []);

  const handleClose = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalDismissed);
    onClose();
  };

  const handlePrimaryAction = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalPrimaryActionClicked);
    onPrimaryAction();
  };

  const handleDownloadTextApp = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalDownloadTextAppClicked);
    downloadTextDesktopApp();
  };

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ModalBase className={styles.modal} fullSpaceContent onClose={handleClose} closeOnEscPress>
        <div className={styles.closeButton}>
          <ModalCloseButton onClick={handleClose} />
        </div>
        <img className={styles.media} src={imageSource} alt="Text App" width={592} height={240} />
        <div className={styles.content}>
          <Heading as="h2" size="lg" className={styles.title}>
            {LEGACY_MIGRATION_MODAL_TITLE}
          </Heading>
          <Text size="md" className={styles.descriptionHighlight}>
            {LEGACY_MIGRATION_MODAL_COPY.descriptionHighlight}
          </Text>
          <Text size="md" className={styles.description}>
            {LEGACY_MIGRATION_MODAL_COPY.description}
          </Text>
          <div className={styles.actions}>
            {isDesktopAppDetected() ? (
              <Button size="large" kind="high-contrast" onClick={handleDownloadTextApp}>
                {LEGACY_MIGRATION_MODAL_COPY.downloadTextApp}
              </Button>
            ) : (
              <Button size="large" kind="high-contrast" onClick={handlePrimaryAction}>
                {LEGACY_MIGRATION_MODAL_COPY.primaryAction}
              </Button>
            )}
          </div>
        </div>
      </ModalBase>
    </ModalPortal>
  );
};
